import { Select } from "@omnitok/ui";
import { CalendarClock } from "lucide-react";

import { useT } from "@/i18n";
import { Field } from "@/pages/builder/components/Field";
import type { UseBuilderDraft } from "@/hooks/useBuilderDraft";
import type { AiHomeTime, AiHomeTimeMode } from "@/types";

/**
 * How the AI Home (portal, ``home.mode = ai``) reads time: always relative
 * to the latest data. A report's rule is REQUIRED at publish (the portal
 * leaves a report without it out of the AI Home); a project's rule is for
 * its base table, offered only when it has one.
 *
 * - day: the latest period (KPIs, rankings); trends read ``periods`` back.
 * - range: every element reads the last ``periods`` periods.
 * - none: no time dimension.
 *
 * The anchor table's ``MAX(date_column)`` is "the latest period";
 * completeness (optional) asks the person before building when the
 * latest period holds fewer rows than ``min_ratio`` of a normal one.
 */
export function AiHomeTimeEditor({
  time,
  onChange,
  title,
  description,
  missing,
  suggestedColumn,
  suggestedTable,
}: {
  time: AiHomeTime | undefined;
  onChange: (next: AiHomeTime | undefined) => void;
  title: string;
  description: string;
  /** Shown when there is no rule (``null`` = nothing to say). */
  missing: string | null;
  suggestedColumn?: string;
  suggestedTable?: string;
}): JSX.Element {
  const { t } = useT();

  function setMode(mode: AiHomeTimeMode | ""): void {
    if (mode === "") {
      onChange(undefined);
      return;
    }
    if (mode === "none") {
      onChange({ mode: "none" });
      return;
    }
    onChange({
      mode,
      date_column: time?.date_column ?? suggestedColumn ?? "",
      anchor_table: time?.anchor_table ?? suggestedTable ?? "",
      range: { periods: time?.range?.periods ?? 30 },
      ...(time?.completeness ? { completeness: time.completeness } : {}),
    });
  }

  function patch(p: Partial<AiHomeTime>): void {
    if (time === undefined) return;
    onChange({ ...time, ...p });
  }

  const dated = time !== undefined && time.mode !== "none";

  return (
    <section className="rounded-[14px] border border-neutral-200 bg-white shadow-card p-5">
      <header className="mb-2 flex items-center gap-2">
        <CalendarClock className="h-4 w-4 text-primary-dark" aria-hidden="true" />
        <h2 className="text-base font-semibold text-neutral-900">{title}</h2>
      </header>
      <p className="mb-4 text-xs text-neutral-500">{description}</p>
      {time === undefined && missing !== null && (
        <p role="alert" className="mb-3 text-xs font-medium text-error-dark">
          {missing}
        </p>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        <Field label={t("editor.ai.aiHomeTime.modeLabel")} hint={t("editor.ai.aiHomeTime.modeHint")}>
          <Select
            options={[
              { value: "", label: t("editor.ai.aiHomeTime.modeUnset") },
              { value: "day", label: t("editor.ai.aiHomeTime.mode.day") },
              { value: "range", label: t("editor.ai.aiHomeTime.mode.range") },
              { value: "none", label: t("editor.ai.aiHomeTime.mode.none") },
            ]}
            value={time?.mode ?? ""}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setMode(e.target.value as AiHomeTimeMode | "")}
            fullWidth
          />
        </Field>
        {dated && (
          <>
            <Field label={t("editor.ai.aiHomeTime.dateColumnLabel")} hint={t("editor.ai.aiHomeTime.dateColumnHint")}>
              <input
                type="text"
                value={time.date_column ?? ""}
                onChange={(e) => patch({ date_column: e.target.value })}
                placeholder="snapshot_date"
                className="block w-full rounded-[10px] border border-control bg-white px-3 py-2 text-sm placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary font-mono"
              />
            </Field>
            <Field label={t("editor.ai.aiHomeTime.anchorLabel")} hint={t("editor.ai.aiHomeTime.anchorHint")}>
              <input
                type="text"
                value={time.anchor_table ?? ""}
                onChange={(e) => patch({ anchor_table: e.target.value })}
                className="block w-full rounded-[10px] border border-control bg-white px-3 py-2 text-sm placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary font-mono"
              />
            </Field>
            <Field label={t("editor.ai.aiHomeTime.periodsLabel")} hint={t("editor.ai.aiHomeTime.periodsHint")}>
              <input
                type="number"
                min={1}
                max={400}
                value={time.range?.periods ?? ""}
                onChange={(e) => {
                  const n = Number.parseInt(e.target.value, 10);
                  if (Number.isFinite(n)) patch({ range: { periods: n } });
                }}
                className="block rounded-[10px] border border-control bg-white px-3 py-2 text-sm placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary w-28"
              />
            </Field>
            <div className="md:col-span-2">
              <label className="flex cursor-pointer items-center gap-2 text-sm text-neutral-800">
                <input
                  type="checkbox"
                  checked={time.completeness !== undefined}
                  onChange={(e) =>
                    patch({ completeness: e.target.checked ? { min_ratio: 0.8, baseline: 7 } : undefined })
                  }
                  className="h-4 w-4 rounded border-neutral-300 text-primary-dark"
                />
                {t("editor.ai.aiHomeTime.completenessToggle")}
              </label>
              <p className="mt-1 text-xs text-neutral-500">
                {time.completeness === undefined
                  ? t("editor.ai.aiHomeTime.completenessOff")
                  : t("editor.ai.aiHomeTime.completenessHint")}
              </p>
            </div>
            {time.completeness !== undefined && (
              <>
                <Field label={t("editor.ai.aiHomeTime.minRatioLabel")}>
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={Math.round(time.completeness.min_ratio * 100)}
                    onChange={(e) => {
                      const n = Number.parseInt(e.target.value, 10);
                      if (Number.isFinite(n) && time.completeness)
                        patch({ completeness: { ...time.completeness, min_ratio: n / 100 } });
                    }}
                    className="block rounded-[10px] border border-control bg-white px-3 py-2 text-sm placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary w-24"
                  />
                </Field>
                <Field label={t("editor.ai.aiHomeTime.baselineLabel")}>
                  <input
                    type="number"
                    min={1}
                    max={60}
                    value={time.completeness.baseline}
                    onChange={(e) => {
                      const n = Number.parseInt(e.target.value, 10);
                      if (Number.isFinite(n) && time.completeness)
                        patch({ completeness: { ...time.completeness, baseline: n } });
                    }}
                    className="block rounded-[10px] border border-control bg-white px-3 py-2 text-sm placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary w-24"
                  />
                </Field>
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
}

/** A report's rule, in the AI tab. */
export function AiHomeTimeSection({ draft }: { draft: UseBuilderDraft }): JSX.Element {
  const { t } = useT();
  return (
    <AiHomeTimeEditor
      time={draft.ai.ai_home?.time}
      onChange={(next) => {
        // Clearing the rule keeps the report's reference elements.
        const { time: _cleared, ...rest } = draft.ai.ai_home ?? {};
        draft.setAI({
          ai_home: next !== undefined ? { ...rest, time: next } : Object.keys(rest).length > 0 ? rest : undefined,
        });
      }}
      title={t("editor.ai.aiHomeTime.title")}
      description={t("editor.ai.aiHomeTime.description")}
      missing={t("editor.ai.aiHomeTime.missing")}
      suggestedColumn={draft.ai.insights_data_config?.date_column}
    />
  );
}
